import './images.css'

import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { usePermissions } from '@refinedev/core'
import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Input, Select, Space, Table, Typography } from 'antd'
import { useState } from 'react'

import { hasPermission } from '@/auth/capabilities'

import {
  type CatalogImage,
  fetchImages,
  type ImageAction,
  IMAGES_KEY,
} from './api'
import { imageColumns } from './columns'
import { ImageActionModal } from './components/ImageActionModal'
import { ImageDetails } from './components/ImageDetails'
import { ImageForm } from './components/ImageForm'

export default function ImagesPage() {
  const { data: permissions = [] } = usePermissions<string[]>({})
  const canRead = hasPermission(permissions, 'admin:default_images:read')
  const canWrite = hasPermission(permissions, 'admin:default_images:write')
  const query = useQuery({
    queryKey: IMAGES_KEY,
    queryFn: ({ signal }) => fetchImages(signal),
    enabled: canRead,
    refetchInterval: 10000,
    retry: false,
  })
  const [search, setSearch] = useState('')
  const [visibility, setVisibility] = useState('active')
  const [editor, setEditor] = useState<{ image?: CatalogImage }>()
  const [action, setAction] = useState<{
    image: CatalogImage
    kind: ImageAction
  }>()
  const images = query.data ?? []
  const term = search.trim().toLowerCase()
  const rows = images.filter((i) => {
    let visible = i.state.status === visibility
    if (visibility === 'all') visible = true
    if (visibility === 'active') visible = i.state.status !== 'REMOVED'
    if (visibility === 'PUBLISHED')
      visible = ['PUBLISHED', 'DEFAULT'].includes(i.state.status)
    return (
      visible &&
      `${i.name} ${i.distribution} ${i.slug} ${i.id}`
        .toLowerCase()
        .includes(term)
    )
  })
  let emptyText = images.length ? 'No matching images' : 'No images yet'
  if (query.error) emptyText = 'Images unavailable'
  return (
    <>
      <Typography.Title level={2} className="image-catalog-heading">
        Images
      </Typography.Title>
      <Typography.Paragraph>
        Manage the OS choices for new disks. Prepared images stay hidden until
        published. HTTP images are selectable on creation.
      </Typography.Paragraph>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          aria-label="Search images"
          placeholder="Search images"
          allowClear
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240 }}
        />
        <Select
          aria-label="Visibility"
          value={visibility}
          onChange={setVisibility}
          style={{ width: 170 }}
          options={[
            { value: 'active', label: 'Not removed' },
            { value: 'all', label: 'All entries' },
            { value: 'PUBLISHED', label: 'Published' },
            { value: 'HIDDEN', label: 'Hidden' },
            { value: 'DEFAULT', label: 'Default' },
            { value: 'REMOVED', label: 'Removed' },
          ]}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            void query.refetch()
          }}
          loading={query.isFetching}
        >
          Refresh
        </Button>
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setEditor({})}
          >
            Create image
          </Button>
        )}
      </Space>
      {query.error && (
        <Alert
          style={{ marginBottom: 16 }}
          role="alert"
          type="error"
          showIcon
          message="Could not refresh images"
          description={query.error.message}
          action={
            <Button
              onClick={() => {
                void query.refetch()
              }}
            >
              Retry
            </Button>
          }
        />
      )}
      {!canWrite && (
        <Typography.Paragraph type="secondary">
          Read-only access. Image changes require admin:default_images:write.
        </Typography.Paragraph>
      )}
      <Table
        rowKey="id"
        dataSource={rows}
        loading={query.isLoading}
        scroll={{ x: 950 }}
        columns={imageColumns(
          canWrite,
          (image) => setEditor({ image }),
          (image, kind) => setAction({ image, kind }),
        )}
        expandable={{
          expandedRowRender: (image) => <ImageDetails image={image} />,
        }}
        locale={{ emptyText }}
      />
      {editor && (
        <ImageForm image={editor.image} onClose={() => setEditor(undefined)} />
      )}
      {action && (
        <ImageActionModal
          image={action.image}
          action={action.kind}
          images={images}
          onClose={() => setAction(undefined)}
        />
      )}
    </>
  )
}
